import { create } from 'apisauce';

import envBaseUrl from './envBaseUrl';

/**
 * Builds a users-service client scoped to a specific environment (dev, stable,
 * staging, app). The Case Copy / Case Diff tools let you pick an environment
 * per side, so their org/user lookups must hit that environment's users
 * service rather than the one the app itself is running against (the
 * `userManagement` singleton is pinned to VUE_APP_USERS_SERVICE_URL and can't
 * do that).
 *
 * Auth uses the currently stored session token; talking to a different
 * environment therefore requires that env to accept the token (fine for local
 * services, and for envs sharing the same auth tenant).
 */
const authResultKey = 'vrpatients::auth::result';

function authHeader() {
  const authResult = JSON.parse(localStorage.getItem(authResultKey));
  return authResult ? `Bearer ${authResult.idToken}` : undefined;
}

function unwrap(response, fallbackMessage) {
  if (!response.ok) {
    throw response.data || new Error(fallbackMessage);
  }
  return response.data;
}

export default function envUsersService(env) {
  const api = create({
    baseURL: `${envBaseUrl(env, 'users')}/api/users`,
    headers: { Authorization: authHeader() },
  });

  return {
    async getOrgs() {
      const data = unwrap(
        await api.get('/extension/groups'),
        'Failed to load organizations'
      );
      return data.groups;
    },
    async getOrgMembers(orgId, includeDeleted = false) {
      const data = unwrap(
        await api.get(`/fetchall/users?orgId=${orgId}&includeDeleted=${includeDeleted}`),
        'Failed to load users'
      );
      // Mirror userManagement.getGroupMembers: drop soft-deleted users.
      return includeDeleted ? data.users : data.users.filter((u) => u.created_at);
    },
    async loginAs(orgId, email) {
      return unwrap(
        await api.post('/auth/loginAs', { orgId, email }),
        'Failed to authenticate as user'
      );
    },
  };
}
